import { useEffect, useMemo, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'

import { BrandKitsAPI } from '@makyoapp/makyo-sdk'
import { OrgPermission } from '@makyoapp/makyo-sdk/models/ACL'
import { Plus16 } from '@makyoapp/makyo-icon/icon'
import { Button, DeleteModal, Loading } from '@makyoapp/makyo-ui'

import { lazyModal } from '@/lib/lazy-load'
import { queryClient, queryKeys } from '@/lib/react-query'
import { toSdkBrandKitBody } from '@/adapters/__project__/BrandKitAdapter'
import { useBrandKitContext } from '@/context/BrandKitContext'
import { useCurrentOrgContext } from '@/context/CurrentOrgContext'
import { useOrgPerm } from '@/hooks/useOrgPerm'
import { useReqError } from '@/hooks/useReqError'
import { BrandKit } from '@/shared/brand-kit'
import { ORG_ROUTE } from '@/shared/constants'
import { useSearch } from '@/shared/makyo-hooks'
import { WrapperProps } from '@/shared/routes'
import Layout from '@/components/Layout'

import { BrandKitCard } from './components/BrandKitCard'

const BasicInfoEditModal = lazyModal(() => import('@/components/BasicInfoEditModal'))
const NewBrandKitModal = lazyModal(() => import('./components/NewBrandKitModal'))

/**
 * Brand Kit Index Page
 */
export default function BrandKitIndexPage({ title }: WrapperProps) {
  const { t } = useTranslation()
  const printAPIError = useReqError()
  const navigate = useNavigate()

  const canSeeBrandKit = useOrgPerm(OrgPermission.BrandKitAccess)
  const canEditBrandKit = useOrgPerm(OrgPermission.BrandKitEdit)

  const { currentOrg } = useCurrentOrgContext()
  const { isLoading, brandKits, onUpdateBrand, onDeleteBrand, refetchList } = useBrandKitContext()

  const [debounceSearch, setDebounceSearch, cancelDebounceSearch, search, setSearch] = useSearch()
  const [del, setDel] = useState<null | { name: string; id: string; error: string | null }>(null)
  const [show, setShow] = useState(false)
  const [loadingAction, setIsLoadingAction] = useState(false)
  const [renameItem, setRenameItem] = useState<BrandKit | null>(null)

  // fetch all apps used in brandkits
  const { data: brandKitAppMap } = useQuery({
    queryKey: [queryKeys.orgBrandkitApps, currentOrg?.id],
    queryFn: () => {
      if (!currentOrg?.id) return
      return BrandKitsAPI.ReqGetBrandKitsApps(currentOrg.id).then(
        (data) => new Map(data.map((app) => [app._id, app._Projects]))
      )
    },
    enabled: !!currentOrg?.id,
  })

  // Redirect if user doesn't have access
  useEffect(() => {
    if (!canSeeBrandKit && currentOrg?.id) {
      navigate(`${ORG_ROUTE}/${currentOrg.id}`, { replace: true })
    }
  }, [canSeeBrandKit, currentOrg?.id, navigate])

  const filtered = useMemo(() => {
    return debounceSearch
      ? brandKits.filter((i) => i.name.toLowerCase().includes(debounceSearch.toLowerCase()))
      : brandKits
  }, [debounceSearch, brandKits])

  // close modal
  const handleCloseRenameModal = () => {
    if (isLoading) return
    setRenameItem(null)
  }

  // rename
  const handleRenameBrandKit = (newName: string) => {
    if (!renameItem) return

    setIsLoadingAction(true)

    let updated = { ...renameItem, name: newName }

    BrandKitsAPI.ReqUpdate(updated.id, toSdkBrandKitBody(updated))
      .then(() => {
        onUpdateBrand(updated.id, updated)

        queryClient.invalidateQueries({
          queryKey: [queryKeys.orgBrandKitDetail, updated.id],
        })
        queryClient.invalidateQueries({
          queryKey: [queryKeys.orgBrandKitDetailV2, updated.id],
        })
        queryClient.invalidateQueries({
          queryKey: [queryKeys.orgBrandKitsV2, currentOrg?.id],
        })
      })
      .catch(printAPIError)
      .finally(() => {
        setIsLoadingAction(false)
        setRenameItem(null)
      })
  }

  // delete
  const handleDeleteBrandKit = (id: string) => {
    setIsLoadingAction(true)

    BrandKitsAPI.ReqDelete(id)
      .then(() => {
        onDeleteBrand(id)
      })
      .catch(printAPIError)
      .finally(() => {
        setIsLoadingAction(false)
        setDel(null)
        refetchList()
      })
  }

  return (
    <Layout
      title={title}
      search={search}
      onSearch={setSearch}
      onClearSearch={() => {
        cancelDebounceSearch()
        setSearch('')
        setDebounceSearch('')
      }}
      actions={
        canEditBrandKit && (
          <>
            <Button icon={<Plus16 />} onClick={() => setShow(true)}>
              <Trans>Add New Brand Kit</Trans>
            </Button>
            <NewBrandKitModal show={show} setShow={setShow} />
          </>
        )
      }
    >
      {isLoading ? (
        <Loading />
      ) : (
        <section className='grids-3 gap-8'>
          {filtered &&
            filtered.map((item) => {
              return (
                <BrandKitCard
                  apps={brandKitAppMap?.get(item.id) || []}
                  key={item.id}
                  brandKit={item}
                  setDel={setDel}
                  setRenameItem={setRenameItem}
                  canDelete={filtered.length > 1}
                />
              )
            })}
          {del && (
            <DeleteModal
              show={true}
              isLoading={loadingAction}
              onHide={() => setDel(null)}
              onSubmit={() => {
                handleDeleteBrandKit(del.id)
              }}
              {...(del.error ? {} : { continueLabel: t('Yes, Delete') })}
              canContinue={!del.error}
              withCancel
              cancelLabel={t('Cancel')}
              title={t('Delete Brand Kit')}
            >
              <div className='modal-sm text-sm'>
                {del.error ? (
                  <div className='text-red-500'>
                    <Trans>{del.error}</Trans>
                  </div>
                ) : (
                  <Trans>Are you sure you&apos;d like to delete this brand kit?</Trans>
                )}
              </div>
            </DeleteModal>
          )}

          {renameItem && (
            <BasicInfoEditModal
              id='org-brandkit-rename-modal'
              show={true}
              name={renameItem.name}
              title={t('Rename Brand Kit')}
              onHide={handleCloseRenameModal}
              isLoading={loadingAction}
              withAvatar={false}
              onSubmit={(newName) => {
                handleRenameBrandKit(newName)
              }}
            />
          )}
        </section>
      )}
    </Layout>
  )
}
